/**
 * Per-user "MFA required at last sign-in" baseline.
 *
 * Enforcement policy (chosen 2026-06-25): a newly-mandated MFA role is enforced
 * at the user's NEXT sign-in, NOT mid-session — so turning on a mandate (or the
 * admin-baseline backfill) never yanks an already-logged-in user into the
 * enrollment wall. Mechanism:
 *
 *   - On a genuine fresh sign-in we snapshot the effective required-role set as
 *     this session's "baseline" (persisted here so it survives reloads).
 *   - The gate forces enrollment only for roles that were required AT sign-in
 *     (in the baseline) AND are still required now (in the live effective set).
 *       · A role added AFTER sign-in is not in the baseline -> grandfathered,
 *         enforced at next sign-in.
 *       · A role removed is no longer in the live set -> released immediately.
 *   - A session with NO baseline (existing session from before this shipped, or
 *     cleared storage) is grandfathered until its next sign-in.
 *
 * This is the CLIENT onboarding gate (UX), not the security boundary — the
 * server-side AAL2 gates remain the teeth. localStorage, keyed by user id so it
 * can't leak across accounts on a shared device. `now`-free / pure.
 */
const PREFIX = 'crewvex.mfaEnrollBaseline.';

/** Roles required at this user's last sign-in, or null if none recorded. */
export function readEnrollmentBaseline(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(PREFIX + userId);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

/** Snapshot the effective required-role set as this session's baseline. */
export function writeEnrollmentBaseline(userId, roles) {
  if (!userId) return;
  try {
    localStorage.setItem(PREFIX + userId, JSON.stringify([...new Set(roles || [])]));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/** Drop the baseline (e.g. on sign-out). Keyed by user, so optional for safety. */
export function clearEnrollmentBaseline(userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(PREFIX + userId);
  } catch (_) {
    /* ignore */
  }
}

/**
 * Roles to actually enforce for this session = baseline ∩ current effective.
 *
 * @param {string[]|null|undefined} baselineRoles  required at sign-in; null/undefined = no baseline -> grandfather (enforce nothing)
 * @param {Set<string>|string[]|null|undefined} currentEffective  live effective required set
 * @returns {Set<string>}
 */
export function enforceableRolesAtSession(baselineRoles, currentEffective) {
  if (!Array.isArray(baselineRoles)) return new Set();
  const current = currentEffective instanceof Set
    ? currentEffective
    : new Set(currentEffective || []);
  return new Set(baselineRoles.filter((role) => current.has(role)));
}
